import React, { useMemo, useState } from 'react';

import { Button, Modal } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  CreateSlotRiderInput,
  SiSlotRiderRole,
  scheduledInstantAPI,
} from '@/services/api/scheduled-instant';

import { ClientPicker, RiderPicker, RiderSelection } from './pickers';

interface CreateSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a slot is created so the parent can refresh its list. */
  onCreated: () => void;
  /** Prefill the date field (defaults to empty). ISO YYYY-MM-DD. */
  defaultDate?: string;
}

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';
const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500';

const MAX_MAIN_RIDERS = 15;

const CreateSlotModal: React.FC<CreateSlotModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  defaultDate,
}) => {
  const [providerId, setProviderId] = useState<number | ''>('');
  const [clientName, setClientName] = useState('');
  const [scheduleDate, setScheduleDate] = useState(defaultDate ?? '');
  const [windowStartTime, setWindowStartTime] = useState('');
  const [windowEndTime, setWindowEndTime] = useState('');
  const [batchLabel, setBatchLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [mainRiders, setMainRiders] = useState<RiderSelection[]>([]);
  const [reserveRiders, setReserveRiders] = useState<RiderSelection[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setProviderId('');
    setClientName('');
    setScheduleDate(defaultDate ?? '');
    setWindowStartTime('');
    setWindowEndTime('');
    setBatchLabel('');
    setNotes('');
    setMainRiders([]);
    setReserveRiders([]);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const validation = useMemo(() => {
    if (providerId === '' || !clientName) return 'Klien wajib dipilih.';
    if (!scheduleDate) return 'Tanggal wajib diisi.';
    if (!windowStartTime) return 'Jam mulai wajib diisi.';
    if (!windowEndTime) return 'Jam selesai wajib diisi.';
    if (windowEndTime <= windowStartTime)
      return 'Jam selesai harus setelah jam mulai.';
    if (mainRiders.length < 1) return 'Minimal satu rider utama.';
    if (mainRiders.length > MAX_MAIN_RIDERS)
      return `Maksimal ${MAX_MAIN_RIDERS} rider utama per slot.`;
    return null;
  }, [
    providerId,
    clientName,
    scheduleDate,
    windowStartTime,
    windowEndTime,
    mainRiders,
  ]);

  const handleSubmit = async () => {
    if (validation || providerId === '') {
      showErrorToast('Belum lengkap', validation ?? 'Lengkapi form.');
      return;
    }
    const riders: CreateSlotRiderInput[] = [
      ...mainRiders.map((r) => ({ ...r, role: 'utama' as SiSlotRiderRole })),
      ...reserveRiders.map((r) => ({
        ...r,
        role: 'cadangan' as SiSlotRiderRole,
      })),
    ];
    setSubmitting(true);
    try {
      await scheduledInstantAPI.createSlot({
        providerId,
        clientName,
        scheduleDate,
        windowStartTime,
        windowEndTime,
        batchLabel: batchLabel.trim() || null,
        notes: notes.trim() || null,
        riders,
      });
      showSuccessToast(
        'Slot dibuat',
        `${clientName} · ${windowStartTime}–${windowEndTime}`
      );
      reset();
      onCreated();
      onClose();
    } catch (e: any) {
      showErrorToast('Gagal membuat slot', e.message || 'Terjadi kesalahan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      icon="ri-calendar-schedule-line"
      iconTone="primary"
      title="Tambah Slot Baru"
      subtitle="Input langsung ke dashboard — jadwal rider harian (Phase 1)"
      width="lg"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={handleClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !!validation}>
            {submitting ? 'Menyimpan…' : 'Buat Slot'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <label className={labelCls}>
            Klien <span className="text-red-500">*</span>
          </label>
          <ClientPicker
            open={isOpen}
            providerId={providerId}
            clientName={clientName}
            onChange={(sel) => {
              setProviderId(sel ? sel.providerId : '');
              setClientName(sel ? sel.clientName : '');
            }}
            disabled={submitting}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="slot-date" className={labelCls}>
              Tanggal <span className="text-red-500">*</span>
            </label>
            <input
              id="slot-date"
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              className={`${inputCls} font-mono`}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="slot-start" className={labelCls}>
              Jam Mulai <span className="text-red-500">*</span>
            </label>
            <input
              id="slot-start"
              type="time"
              value={windowStartTime}
              onChange={(e) => setWindowStartTime(e.target.value)}
              className={`${inputCls} font-mono`}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="slot-end" className={labelCls}>
              Jam Selesai <span className="text-red-500">*</span>
            </label>
            <input
              id="slot-end"
              type="time"
              value={windowEndTime}
              onChange={(e) => setWindowEndTime(e.target.value)}
              className={`${inputCls} font-mono`}
              disabled={submitting}
            />
          </div>
        </div>

        <RiderPicker
          open={isOpen}
          title="Rider Utama"
          required
          riders={mainRiders}
          onChange={setMainRiders}
          disabled={submitting}
        />
        <RiderPicker
          open={isOpen}
          title="Rider Cadangan"
          hint="opsional — first-call jika rider utama TIDAK / No Response"
          riders={reserveRiders}
          onChange={setReserveRiders}
          disabled={submitting}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="slot-batch" className={labelCls}>
              Batch{' '}
              <span className="normal-case font-medium tracking-normal text-gray-400">
                (opsional)
              </span>
            </label>
            <input
              id="slot-batch"
              type="text"
              value={batchLabel}
              onChange={(e) => setBatchLabel(e.target.value)}
              placeholder="cth. Pagi"
              className={inputCls}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="slot-notes" className={labelCls}>
              Catatan Ops{' '}
              <span className="normal-case font-medium tracking-normal text-gray-400">
                (opsional)
              </span>
            </label>
            <input
              id="slot-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="cth. Pickup di gudang belakang"
              className={inputCls}
              disabled={submitting}
            />
          </div>
        </div>

        {validation && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-gsm px-3 py-2">
            {validation}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CreateSlotModal;
